import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Image,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { publicAPI } from '../services/api';

const { Text, Title } = Typography;

const COPY = {
  zh: {
    title: 'AOZ 充值',
    subtitle: '当前账号与大区自动读取，选择档位后直接支付到账。',
    account: '当前账号',
    server: '当前大区',
    role: '当前角色',
    roleId: '角色ID',
    products: '充值档位',
    payment: '支付方式',
    payNow: '确认充值并支付',
    loadingAccount: '正在读取当前登录账号',
    noLogin: '未获取到当前游戏登录态，请返回游戏重新登录后再充值。',
    noRole: '未找到当前登录角色',
    noProduct: '暂无 AOZ 充值档位',
    reload: '重新加载',
    total: '应付金额',
    secure: '安全交易',
    instant: '即时到账',
    service: '专属客服',
    selected: '已选',
    stock: '库存',
    unlimited: '充足',
    chooseRole: '选择角色',
    choosePayment: '请选择支付方式',
    chooseProduct: '请选择充值档位',
    orderFailed: '创建充值订单失败'
  },
  en: {
    title: 'AOZ Recharge',
    subtitle: 'Account and server are loaded automatically before payment.',
    account: 'Account',
    server: 'Server',
    role: 'Role',
    roleId: 'Role ID',
    products: 'Recharge Packs',
    payment: 'Payment',
    payNow: 'Pay Now',
    loadingAccount: 'Loading account',
    noLogin: 'Login state was not found. Please log in again from the game.',
    noRole: 'No role found',
    noProduct: 'No AOZ packs available',
    reload: 'Reload',
    total: 'Total',
    secure: 'Secure',
    instant: 'Instant delivery',
    service: 'Support',
    selected: 'Selected',
    stock: 'Stock',
    unlimited: 'Enough',
    chooseRole: 'Select role',
    choosePayment: 'Select payment method',
    chooseProduct: 'Select a pack',
    orderFailed: 'Failed to create order'
  },
  ja: {
    title: 'AOZ チャージ',
    subtitle: 'アカウントとサーバーを自動取得して、そのまま支払えます。',
    account: 'アカウント',
    server: 'サーバー',
    role: 'キャラ',
    roleId: 'キャラID',
    products: 'チャージ',
    payment: '支払い',
    payNow: '支払う',
    loadingAccount: 'アカウント確認中',
    noLogin: 'ログイン情報がありません。ゲームから再ログインしてください。',
    noRole: 'キャラが見つかりません',
    noProduct: 'AOZ 商品がありません',
    reload: '再読み込み',
    total: '合計',
    secure: '安全',
    instant: '即時反映',
    service: 'サポート',
    selected: '選択中',
    stock: '在庫',
    unlimited: '十分',
    chooseRole: 'キャラ選択',
    choosePayment: '支払い方法を選択',
    chooseProduct: '商品を選択',
    orderFailed: '注文作成に失敗しました'
  },
  ko: {
    title: 'AOZ 충전',
    subtitle: '현재 계정과 서버를 자동으로 불러온 뒤 결제합니다.',
    account: '계정',
    server: '서버',
    role: '캐릭터',
    roleId: '캐릭터 ID',
    products: '충전 상품',
    payment: '결제',
    payNow: '결제하기',
    loadingAccount: '계정 확인 중',
    noLogin: '로그인 정보가 없습니다. 게임에서 다시 로그인해 주세요.',
    noRole: '캐릭터를 찾을 수 없습니다',
    noProduct: 'AOZ 상품이 없습니다',
    reload: '새로고침',
    total: '합계',
    secure: '안전 결제',
    instant: '즉시 지급',
    service: '고객 지원',
    selected: '선택됨',
    stock: '재고',
    unlimited: '충분',
    chooseRole: '캐릭터 선택',
    choosePayment: '결제 수단을 선택하세요',
    chooseProduct: '상품을 선택하세요',
    orderFailed: '주문 생성 실패'
  }
};

const pageStyle = {
  minHeight: '100vh',
  padding: '20px',
  background: 'linear-gradient(180deg, #edf4ff 0%, #f7f9fc 42%, #ffffff 100%)'
};

const shellStyle = {
  maxWidth: 1120,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 16
};

const heroStyle = {
  overflow: 'hidden',
  borderRadius: 8,
  padding: 22,
  color: '#fff',
  background: 'linear-gradient(135deg, #07111f 0%, #0e2f5c 56%, #0f766e 100%)',
  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.18)'
};

const panelStyle = {
  borderRadius: 8,
  border: '1px solid #dbe4ef',
  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)'
};

function getLanguage() {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = (params.get('lang') || params.get('sdkLang') || 'zh').toLowerCase();
    if (value.startsWith('en')) return 'en';
    if (value.startsWith('ja') || value.startsWith('jp')) return 'ja';
    if (value.startsWith('ko') || value.startsWith('kr')) return 'ko';
  } catch (error) {
    return 'zh';
  }
  return 'zh';
}

function getAuthKey() {
  try {
    const params = new URLSearchParams(window.location.search);
    const queryAuth = params.get('authKey') || params.get('auth_key') || params.get('token') || '';
    if (queryAuth) {
      localStorage.setItem('mall_auth_key', queryAuth);
      return queryAuth;
    }
    if (window.AndroidSDK && typeof window.AndroidSDK.getAuthKey === 'function') {
      const sdkAuth = window.AndroidSDK.getAuthKey();
      if (sdkAuth) return sdkAuth;
    }
    return localStorage.getItem('mall_auth_key') || '';
  } catch (error) {
    return '';
  }
}

function isAozProduct(product) {
  const type = String(product.type || '').toLowerCase();
  const name = String(product.name || '').toLowerCase();
  return type === 'aoz' || name.includes('aoz') || name.includes('充值');
}

function productPrice(product) {
  return Number(product.currency_price || product.current_price || 0);
}

function money(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
}

function roleKey(role) {
  return `${role?.game_user_id || role?.character_id || ''}-${role?.server_id || 0}`;
}

function Mall() {
  const lang = useMemo(getLanguage, []);
  const t = COPY[lang] || COPY.zh;
  const [loading, setLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [account, setAccount] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentChannel, setPaymentChannel] = useState('');
  const [firstCharge, setFirstCharge] = useState(null);
  const [accountError, setAccountError] = useState('');

  const selectedProduct = useMemo(
    () => products.find((item) => Number(item.id) === Number(selectedProductId)) || null,
    [products, selectedProductId]
  );

  const selectedRole = useMemo(
    () => roles.find((item) => roleKey(item) === selectedRoleId) || roles[0] || null,
    [roles, selectedRoleId]
  );

  const activeProvider = useMemo(
    () => paymentProviders.find((provider) => String(provider.id) === String(paymentMethod)) || paymentProviders[0] || null,
    [paymentProviders, paymentMethod]
  );

  const paymentTypes = useMemo(
    () => (Array.isArray(activeProvider?.paymentTypes) ? activeProvider.paymentTypes : []),
    [activeProvider]
  );
  const activeChannel = paymentTypes.some((item) => String(item.id) === String(paymentChannel))
    ? paymentChannel
    : (activeProvider?.defaultType || paymentTypes[0]?.id || '');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await publicAPI.getMallProducts({ currency_type: 'CNY' });
      const allProducts = response.data?.data || [];
      const aozProducts = allProducts.filter(isAozProduct);
      const nextProducts = aozProducts.length > 0 ? aozProducts : allProducts;
      setProducts(nextProducts);
      setSelectedProductId((current) => (
        nextProducts.some((item) => Number(item.id) === Number(current)) ? current : (nextProducts[0]?.id || null)
      ));
    } catch (error) {
      message.error(error.response?.data?.message || error.message || t.noProduct);
    } finally {
      setLoading(false);
    }
  }, [t.noProduct]);

  const loadAccount = useCallback(async () => {
    const authKey = getAuthKey();
    if (!authKey) {
      setAccountError(t.noLogin);
      setAccount(null);
      setRoles([]);
      return;
    }
    setAccountLoading(true);
    setAccountError('');
    try {
      const response = await publicAPI.getCurrentMallAccount({ authKey });
      const data = response.data?.data || {};
      const firstChargeResponse = await publicAPI.getFirstCharge({ authKey });
      const nextRoles = Array.isArray(data.roles) ? data.roles : (data.role ? [data.role] : []);
      setAccount({ ...data, authKey });
      setFirstCharge(firstChargeResponse.data?.data || null);
      setRoles(nextRoles);
      const preferred = data.role || nextRoles[0] || null;
      setSelectedRoleId(preferred ? roleKey(preferred) : '');
      if (!preferred) {
        setAccountError(t.noRole);
      }
    } catch (error) {
      setAccountError(error.response?.data?.message || error.message || t.noLogin);
      setFirstCharge(null);
    } finally {
      setAccountLoading(false);
    }
  }, [t.noLogin, t.noRole]);

  const firstChargeBonus = useCallback((product) => {
    if (!firstCharge || firstCharge.available === false) {
      return 0;
    }
    try {
      const claimedProductIds = new Set(
        (Array.isArray(firstCharge.claimed_product_ids) ? firstCharge.claimed_product_ids : [])
          .map((id) => Number(id))
      );
      if (claimedProductIds.has(Number(product.id))) {
        return 0;
      }
      const itemData = typeof product.item_data === 'string' ? JSON.parse(product.item_data || '{}') : (product.item_data || {});
      if (Object.prototype.hasOwnProperty.call(itemData, 'first_charge_bonus_aoz')) {
        return Math.max(0, Math.floor(Number(itemData.first_charge_bonus_aoz) || 0));
      }
      if (!firstCharge.enabled) {
        return 0;
      }
      if (productPrice(product) < Number(firstCharge.minimum_amount || 0)) {
        return 0;
      }
      const baseAoz = (Array.isArray(itemData.items) ? itemData.items : []).reduce((total, item) => {
        const itemType = String(item?.type || '').toLowerCase();
        return total + ((itemType === 'aoz' || itemType === 'aozcoin' || Number(item?.id || 0) === 67174445)
          ? Math.max(0, Number(item?.count || 0))
          : 0);
      }, 0);
      return Math.floor((baseAoz * Math.max(0, Number(firstCharge.bonus_percent || 0))) / 100);
    } catch (error) {
      return 0;
    }
  }, [firstCharge]);

  const loadPaymentProviders = useCallback(async () => {
    try {
      const response = await publicAPI.getPaymentProviders();
      const providers = Array.isArray(response.data?.data) ? response.data.data : [];
      setPaymentProviders(providers);
      setPaymentMethod((current) => (
        providers.some((provider) => String(provider.id) === String(current)) ? current : (providers[0]?.id || '')
      ));
      setPaymentChannel((current) => current || providers[0]?.defaultType || providers[0]?.paymentTypes?.[0]?.id || '');
    } catch (error) {
      setPaymentProviders([]);
      setPaymentMethod('');
      setPaymentChannel('');
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadAccount();
    loadPaymentProviders();
  }, [loadProducts, loadAccount, loadPaymentProviders]);

  useEffect(() => {
    if (paymentTypes.length > 0 && !paymentTypes.some((item) => String(item.id) === String(paymentChannel))) {
      setPaymentChannel(activeProvider?.defaultType || paymentTypes[0]?.id || '');
    }
  }, [activeProvider, paymentChannel, paymentTypes]);

  const submitOrder = async () => {
    if (!selectedProduct) {
      message.warning(t.chooseProduct);
      return;
    }
    if (!selectedRole || !account) {
      message.warning(t.noRole);
      return;
    }
    if (!paymentMethod) {
      message.warning(t.choosePayment);
      return;
    }
    setPaying(true);
    try {
      const response = await publicAPI.createOrder({
        authKey: account.authKey,
        items: [{ product_id: selectedProduct.id, quantity: 1 }],
        user_id: account.user_id || account.account_id || account.root_account_id,
        character_id: selectedRole.character_id || selectedRole.game_user_id,
        character_name: selectedRole.character_name,
        server_id: selectedRole.server_id,
        payment_method: paymentMethod,
        payment_channel: activeChannel
      });
      const order = response.data?.data || {};
      if (order.pay_url) {
        try {
          sessionStorage.setItem('last_payment_order', JSON.stringify({
            order_no: order.order_no || '',
            trade_no: order.trade_no || '',
            payment_provider: order.payment_provider || order.payment_method || '',
            payment_channel: order.payment_channel || activeChannel || ''
          }));
        } catch (storageError) {
          // Query parameters from the payment provider remain the source of truth.
        }
        window.location.href = order.pay_url;
        return;
      }
      message.success(t.payNow);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || t.orderFailed);
    } finally {
      setPaying(false);
    }
  };

  const renderProduct = (product) => {
    const selected = Number(product.id) === Number(selectedProductId);
    const stockText = Number(product.stock) === -1 ? t.unlimited : `${t.stock} ${product.stock}`;
    const bonusAoz = firstChargeBonus(product);
    return (
      <Col key={product.id} xs={24} sm={12} lg={8}>
        <button
          type="button"
          onClick={() => setSelectedProductId(product.id)}
          style={{
            width: '100%',
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          <Card
            hoverable
            style={{
              ...panelStyle,
              minHeight: 214,
              borderColor: selected ? '#2563eb' : '#dbe4ef',
              boxShadow: selected ? '0 14px 32px rgba(37, 99, 235, 0.18)' : panelStyle.boxShadow
            }}
            bodyStyle={{ padding: 16 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ display: 'block', fontSize: 16 }} ellipsis>
                  {product.name}
                </Text>
                <Space size={6} wrap style={{ marginTop: 8 }}>
                  {product.is_hot === 1 ? <Tag color="red">HOT</Tag> : null}
                  {product.is_new === 1 ? <Tag color="green">NEW</Tag> : null}
                  {bonusAoz > 0 ? <Tag color="volcano">首充 +{bonusAoz} AOZ</Tag> : null}
                  <Tag color="blue">AOZ</Tag>
                </Space>
              </div>
              {selected ? <CheckCircleOutlined style={{ color: '#2563eb', fontSize: 20 }} /> : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #0f172a, #1d4ed8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 800,
                  flexShrink: 0
                }}
              >
                {product.image_url ? (
                  <Image src={product.image_url} alt={product.name} preview={false} width={62} height={62} style={{ objectFit: 'contain' }} />
                ) : (
                  'AOZ'
                )}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div>
                  {Number(product.original_price) > 0 && Number(product.original_price) !== productPrice(product) ? (
                    <Text delete type="secondary" style={{ marginRight: 8 }}>¥{money(product.original_price)}</Text>
                  ) : null}
                  <Text strong style={{ color: '#dc2626', fontSize: 24 }}>¥{money(productPrice(product))}</Text>
                </div>
                <Text type="secondary" ellipsis style={{ display: 'block', marginTop: 6 }}>
                  {product.description || stockText}
                </Text>
              </div>
            </div>
          </Card>
        </button>
      </Col>
    );
  };

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        <div style={heroStyle}>
          <Row gutter={[18, 18]} align="middle" justify="space-between">
            <Col xs={24} md={13}>
              <Space direction="vertical" size={8}>
                <Tag color="blue" style={{ width: 'fit-content' }}>AOZ</Tag>
                <Title level={2} style={{ color: '#fff', margin: 0 }}>{t.title}</Title>
                <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: 15 }}>{t.subtitle}</Text>
              </Space>
            </Col>
            <Col xs={24} md={11}>
              <Row gutter={[10, 10]}>
                {[
                  [<SafetyCertificateOutlined />, t.secure],
                  [<ThunderboltOutlined />, t.instant],
                  [<CustomerServiceOutlined />, t.service]
                ].map(([icon, label]) => (
                  <Col key={label} span={8}>
                    <div style={{ minHeight: 72, borderRadius: 8, padding: 10, background: 'rgba(255,255,255,0.11)', border: '1px solid rgba(255,255,255,0.16)' }}>
                      <div style={{ color: '#93c5fd', fontSize: 20 }}>{icon}</div>
                      <div style={{ color: '#fff', fontSize: 13, marginTop: 6 }}>{label}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </div>

        <Row gutter={[16, 16]} align="top">
          <Col xs={24} lg={15}>
            <Card title={<Space><WalletOutlined />{t.products}</Space>} style={panelStyle} extra={<Button icon={<ReloadOutlined />} onClick={loadProducts} loading={loading}>{t.reload}</Button>}>
              <Spin spinning={loading}>
                {products.length === 0 ? (
                  <Empty description={t.noProduct} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Row gutter={[12, 12]}>
                    {products.map(renderProduct)}
                  </Row>
                )}
              </Spin>
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <Card title={<Space><UserOutlined />{t.account}</Space>} style={panelStyle}>
              <Spin spinning={accountLoading} tip={t.loadingAccount}>
                {accountError ? (
                  <Alert type="warning" showIcon message={accountError} action={<Button size="small" onClick={loadAccount}>{t.reload}</Button>} />
                ) : (
                  <Space direction="vertical" size={14} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar icon={<UserOutlined />} style={{ background: '#2563eb' }} />
                      <div style={{ minWidth: 0 }}>
                        <Text strong style={{ display: 'block' }} ellipsis>{account?.account || '-'}</Text>
                        <Text type="secondary">{selectedRole?.server_name || `S${selectedRole?.server_id || '-'}`}</Text>
                      </div>
                    </div>
                    {roles.length > 1 ? (
                      <Select
                        value={selectedRoleId}
                        onChange={setSelectedRoleId}
                        style={{ width: '100%' }}
                        placeholder={t.chooseRole}
                        options={roles.map((role) => ({
                          value: roleKey(role),
                          label: `${role.server_name || `S${role.server_id || '-'}`} / ${role.character_name || '-'}`
                        }))}
                      />
                    ) : null}
                    <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', rowGap: 8, columnGap: 12 }}>
                      <Text type="secondary">{t.account}</Text><Text strong>{account?.account || '-'}</Text>
                      <Text type="secondary">{t.server}</Text><Text strong>{selectedRole?.server_name || `S${selectedRole?.server_id || '-'}`}</Text>
                      <Text type="secondary">{t.role}</Text><Text strong>{selectedRole?.character_name || '-'}</Text>
                      <Text type="secondary">{t.roleId}</Text><Text code>{selectedRole?.character_id || selectedRole?.game_user_id || '-'}</Text>
                    </div>
                  </Space>
                )}
              </Spin>

              <Divider />

              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text strong><CreditCardOutlined style={{ marginRight: 8 }} />{t.payment}</Text>
                {paymentProviders.length === 0 ? (
                  <Alert type="warning" showIcon message={t.choosePayment} />
                ) : (
                  <Radio.Group value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} style={{ width: '100%' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {paymentProviders.map((provider) => (
                        <Radio.Button key={provider.id} value={provider.id} style={{ width: '100%', height: 42, lineHeight: '40px', borderRadius: 8 }}>
                          {provider.name || provider.id}
                        </Radio.Button>
                      ))}
                    </Space>
                  </Radio.Group>
                )}
                {paymentTypes.length > 0 ? (
                  <Select
                    value={activeChannel}
                    onChange={setPaymentChannel}
                    style={{ width: '100%' }}
                    options={paymentTypes.map((item) => ({ value: item.id, label: item.name || item.id }))}
                  />
                ) : null}
              </Space>

              <Divider />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <Text type="secondary">{t.total}</Text>
                <Text strong style={{ color: '#dc2626', fontSize: 28 }}>¥{money(productPrice(selectedProduct || {}))}</Text>
              </div>
              <Button
                type="primary"
                size="large"
                block
                loading={paying}
                disabled={!selectedProduct || !selectedRole || !paymentMethod}
                onClick={submitOrder}
                style={{ height: 50, borderRadius: 8, background: '#0f172a' }}
              >
                {t.payNow}
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default Mall;
